import QRCode from "qrcode";
import toast from "react-hot-toast";
import { getZatcaPrintInfo } from "../../services/ZatcaApiServices";

// ZATCA (Fatoora) simplified-invoice QR: base64 of TLV fields
//   1 seller name, 2 seller VAT number, 3 invoice timestamp,
//   4 invoice total incl. VAT, 5 VAT total.
// The length byte counts UTF-8 bytes, not characters, so Arabic names encode correctly.
const tlv = (tag, value) => {
  const bytes = new TextEncoder().encode(value == null ? "" : String(value));
  const len = Math.min(bytes.length, 255);
  const out = new Uint8Array(2 + len);
  out[0] = tag;
  out[1] = len;
  out.set(bytes.subarray(0, len), 2);
  return out;
};

const toBase64 = (bytes) => {
  let s = "";
  bytes.forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s);
};

const pad = (n) => String(n).padStart(2, "0");

// Invoice date as picked on the form, with the current time of day, in local time:
// "2026-10-23T14:05:09". toISOString() would shift a midnight date into the previous day.
export const zatcaTimestamp = (dateValue) => {
  const d = dateValue ? new Date(dateValue) : new Date();
  const day = isNaN(d) ? new Date() : d;
  const now = new Date();
  return (
    `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
  );
};

export const zatcaQrBase64 = ({ sellerName, vatNumber, timestamp, total, vat }) => {
  const parts = [
    tlv(1, sellerName),
    tlv(2, vatNumber),
    tlv(3, timestamp),
    tlv(4, Number(total || 0).toFixed(2)),
    tlv(5, Number(vat || 0).toFixed(2)),
  ];
  const all = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  parts.forEach((p) => {
    all.set(p, at);
    at += p.length;
  });
  return toBase64(all);
};

// The QR image (data URL) for a bill print, or "" when the branch setting
// "Generate QR code in bill print" is off, so the print leaves the QR out.
export const zatcaQrForPrint = async ({ enabled, ...fields }) => {
  if (!enabled) return "";
  return QRCode.toDataURL(zatcaQrBase64(fields));
};

/**
 * The QR for a sales invoice / return print.
 *
 * When the branch is on ZATCA Phase 2 (ZATCA Setup), the API signs the invoice
 * (once) and hands back ZATCA's QR, which is always printed. If that is not
 * possible yet (a standard invoice not cleared, a rule the invoice breaks), the
 * Phase-1 QR is printed and the reason shown. Off ZATCA, `phase1` decides as before.
 *
 * `sale`: { salesGuid } or, right after saving, { branchID, entryNumber, transType: "SI" | "SR" }.
 */
export const zatcaPrintQr = async (sale, phase1) => {
  try {
    if (sale?.salesGuid || (sale?.branchID && sale?.entryNumber)) {
      const info = await getZatcaPrintInfo(sale);
      if (info?.enabled) {
        if (info.qrCode) return QRCode.toDataURL(info.qrCode);
        if (info.message) toast.error(`ZATCA: ${info.message}`, { duration: 8000 });
        return zatcaQrForPrint({ ...phase1, enabled: true });
      }
    }
  } catch (error) {
    // API without the ZATCA module, or not reachable: print as before
  }
  return zatcaQrForPrint(phase1);
};
